/**
 * announcerMatch — pure per-frame derivation of SELF match-event announcer
 * lines from network snapshots (kills / streaks / first blood / death / lead
 * changes / power-up & weapon pickups / incoming homing rockets / round-clock
 * thresholds).
 *
 * Everything is diffed against the previous frame; the tracker never touches
 * audio or the DOM — `Game`'s loop feeds it the live `NetworkSnapshot` (which
 * is structurally compatible with {@link AnnouncerFrame}) and forwards the
 * returned line ids to {@link announcer.announce}. Round boundaries are pushed
 * in explicitly by the quickmatch/hosted wiring (`roundStarted`/`roundEnded`),
 * with an implicit score-reset fallback for the drop-in paths that have no
 * round messages.
 */

import type { AnnouncerLineId } from "./announcer.js";

export interface AnnouncerFramePlayer {
  score: number;
  alive: boolean;
  armour?: number;
  overdriveUntil?: number;
  spread5Until?: number;
  rocketAmmo?: number;
  ricochetShots?: number;
  shockwaveCharges?: number;
}

export interface AnnouncerFrameProjectile {
  ownerId: string;
  isHoming: boolean;
  homingTargetId?: string;
}

export interface AnnouncerFrame {
  ownId: string;
  players: ReadonlyMap<string, AnnouncerFramePlayer>;
  projectiles?: ReadonlyMap<string, AnnouncerFrameProjectile>;
}

/** Tracker-side re-lead cooldown (the line itself also has a 10 s cooldown). */
const LEAD_COOLDOWN_MS = 10_000;

interface OwnSnapshot {
  score: number;
  alive: boolean;
  armour: number;
  overdriveUntil: number;
  spread5Until: number;
  rocketAmmo: number;
  ricochetShots: number;
  shockwaveCharges: number;
}

export class MatchAnnouncerTracker {
  private prevOwn: OwnSnapshot | null = null;
  private prevTotalScore = 0;
  private anyKillThisRound = false;
  private streak = 0;
  private saidStreak3 = false;
  private saidStreak5 = false;
  private wasLeader = false;
  private lastLeadAt = Number.NEGATIVE_INFINITY;
  private roundEndsAt = 0;
  private prevRemainingMs = Number.POSITIVE_INFINITY;
  private said60 = false;
  private said10 = false;
  private warnedRockets = new Set<string>();

  /**
   * A round we play in just started. `endsAtMs` (local epoch) arms the
   * time_60/time_10 thresholds; pass 0/omit when the duration is unknown.
   */
  roundStarted(endsAtMs = 0): void {
    this.resetRoundState();
    this.prevOwn = null; // fresh baseline — score resets must not read as kills
    this.roundEndsAt = endsAtMs;
  }

  /** The round is over — disarm the clock (scores may linger in the snapshot). */
  roundEnded(): void {
    this.roundEndsAt = 0;
  }

  /**
   * Per-frame diff. Returns announcer lines to speak, in the order they
   * should be requested (the scheduler handles priority from there).
   */
  update(frame: AnnouncerFrame, now: number = Date.now()): AnnouncerLineId[] {
    const out: AnnouncerLineId[] = [];
    if (!frame.ownId) return out;
    const me = frame.players.get(frame.ownId);
    if (!me) return out;

    let totalScore = 0;
    frame.players.forEach((p) => { totalScore += p.score; });

    // Implicit round reset (drop-in rooms have no round messages): our score
    // or the arena's total going DOWN can only be a server-side round reset.
    if (this.prevOwn && (me.score < this.prevOwn.score || totalScore < this.prevTotalScore)) {
      this.resetRoundState();
      this.prevOwn = null;
    }

    const prev = this.prevOwn;

    // ── Own kills → first_blood / streaks / kill ──────────────────────────
    if (prev && me.score > prev.score) {
      const firstOfRound = !this.anyKillThisRound;
      this.streak += me.score - prev.score;
      if (firstOfRound) {
        out.push("first_blood");
      } else if (this.streak >= 5 && !this.saidStreak5) {
        this.saidStreak5 = true;
        out.push("kill_streak_5");
      } else if (this.streak >= 3 && !this.saidStreak3) {
        this.saidStreak3 = true;
        out.push("kill_streak_3");
      } else {
        out.push("kill"); // suppressed above whenever a streak line fires
      }
    }

    // ── Own death (streak resets; respawn is silent) ──────────────────────
    if (prev && prev.alive && !me.alive) {
      out.push("death");
      this.streak = 0;
      this.saidStreak3 = false;
      this.saidStreak5 = false;
    }

    // ── Lead change: self becomes the STRICT score leader ─────────────────
    let leader = me.score > 0;
    if (leader) {
      frame.players.forEach((p, id) => {
        if (id !== frame.ownId && p.score >= me.score) leader = false;
      });
    }
    if (leader && !this.wasLeader && prev !== null && now - this.lastLeadAt >= LEAD_COOLDOWN_MS) {
      this.lastLeadAt = now;
      out.push("lead_taken");
    }
    this.wasLeader = leader;

    // ── Self pickups (server-visible field increases) ─────────────────────
    if (prev) {
      if ((me.overdriveUntil ?? 0) > prev.overdriveUntil) out.push("powerup_overdrive");
      if ((me.spread5Until ?? 0) > prev.spread5Until) out.push("powerup_spread");
      if ((me.armour ?? 0) > prev.armour) out.push("powerup_armour");
      if ((me.rocketAmmo ?? 0) > prev.rocketAmmo) out.push("pickup_rocket");
      if ((me.ricochetShots ?? 0) > prev.ricochetShots) out.push("pickup_ricochet");
      if ((me.shockwaveCharges ?? 0) > prev.shockwaveCharges) out.push("pickup_shockwave");
    }

    // ── Incoming homing rocket locked onto SELF (once per projectile) ─────
    if (frame.projectiles) {
      let warned = false;
      frame.projectiles.forEach((p, id) => {
        if (warned) return;
        if (!p.isHoming || p.homingTargetId !== frame.ownId) return;
        if (p.ownerId === frame.ownId) return;
        if (this.warnedRockets.has(id)) return;
        this.warnedRockets.add(id);
        warned = true;
        out.push("rocket_warning");
      });
      if (this.warnedRockets.size > 128) this.warnedRockets.clear();
    }

    // ── Round clock thresholds (once each per round, on the crossing) ─────
    // The first observation only baselines (`prevRemainingMs` finite check):
    // a mid-round joiner already under a threshold must not hear it late.
    if (this.roundEndsAt > 0) {
      const remaining = this.roundEndsAt - now;
      const havePrev = Number.isFinite(this.prevRemainingMs);
      if (!this.said60 && havePrev && this.prevRemainingMs > 60_000 && remaining <= 60_000 && remaining > 0) {
        this.said60 = true;
        out.push("time_60");
      }
      if (!this.said10 && havePrev && this.prevRemainingMs > 10_000 && remaining <= 10_000 && remaining > 0) {
        this.said10 = true;
        out.push("time_10");
      }
      this.prevRemainingMs = remaining;
    }

    // ── Baselines for the next frame ──────────────────────────────────────
    this.prevOwn = {
      score: me.score,
      alive: me.alive,
      armour: me.armour ?? 0,
      overdriveUntil: me.overdriveUntil ?? 0,
      spread5Until: me.spread5Until ?? 0,
      rocketAmmo: me.rocketAmmo ?? 0,
      ricochetShots: me.ricochetShots ?? 0,
      shockwaveCharges: me.shockwaveCharges ?? 0,
    };
    this.prevTotalScore = totalScore;
    if (totalScore > 0) this.anyKillThisRound = true;

    return out;
  }

  private resetRoundState(): void {
    this.anyKillThisRound = false;
    this.streak = 0;
    this.saidStreak3 = false;
    this.saidStreak5 = false;
    this.wasLeader = false;
    this.prevTotalScore = 0;
    this.roundEndsAt = 0;
    this.prevRemainingMs = Number.POSITIVE_INFINITY;
    this.said60 = false;
    this.said10 = false;
    this.warnedRockets.clear();
  }
}

/** Shared instance: fed by Game's loop, reset by the quickmatch/lobby wiring. */
export const matchAnnouncer = new MatchAnnouncerTracker();
